import React, { useEffect, useState } from 'react';
import '@/styles/theme.css';
import '@/styles/dashboard-colors.css';
import '@/styles/provatio-ecosystem-colors.css';
import { Route, Routes, BrowserRouter as Router, Outlet, Navigate, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthProvider } from './contexts/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import { LanguageProvider } from './providers/LanguageProvider';
import { Toaster } from './components/ui/toaster';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { APP_CONFIG } from '@/constants/branding';

// Auth Hook
import { useAuth as useAuthHook } from './hooks/useAuth';
import { Loader2, AlertTriangle } from 'lucide-react';

// Common
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import SystemHealthCheck from './pages/SystemHealthCheck';

// Layout
import Header from './components/Header';
import SystemNavbar from './components/SystemNavbar';

// System Pages
import SystemDashboard from './pages/SystemDashboard';
import ProcessosSystemPage from './pages/system/ProcessosSystemPage';
import TarefasSystemPage from './pages/system/TarefasSystemPage';
import DocumentosSystemPage from './pages/system/DocumentosSystemPage';
import AgendaSystemPage from './pages/system/AgendaSystemPage';
import FinanceiroSystemPage from './pages/system/FinanceiroSystemPage';
import RelatoriosSystemPage from './pages/system/RelatoriosSystemPage';
import HistoricoSystemPage from './pages/system/HistoricoSystemPage';
import AuditoriaSystemPage from './pages/system/AuditoriaSystemPage';

// New Cadastros Hub and Pages
import CadastrosSystemPage from './pages/system/CadastrosSystemPage';
import PessoasPage from './pages/cadastros/PessoasPage';
import TipoPessoaPage from './pages/cadastros/TipoPessoaPage';
import PalavraChavePage from './pages/cadastros/PalavraChavePage';
import ComarcaVaraPage from './pages/cadastros/ComarcaVaraPage';
import SituacaoProcessoPage from './pages/cadastros/SituacaoProcessoPage';
import NaturezaProcessoPage from './pages/cadastros/NaturezaProcessoPage';
import AcaoObjetoPage from './pages/cadastros/AcaoObjetoPage';
import StatusImpugnacaoPage from './pages/cadastros/StatusImpugnacaoPage';

// Modules
import AcademyPage from './pages/Academy/AcademyPage';
import CertificacaoPage from './pages/Certificacao/CertificacaoPage';
import LabsPage from './pages/LabsPage';
import ResearchPage from './pages/ResearchPage';
import AssistenteTecnicoPage from './pages/AssistenteTecnicoPage';
import ComplianceDashboard from './pages/compliance/ComplianceDashboard';
import IntelligenceDashboard from './pages/intelligence/IntelligenceDashboard';
import MultiespecialistasPage from './pages/MultiespecialistasPage';
import AjudaPage from './pages/AjudaPage';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';

const AppLayout = () => {
  const { loading } = useAuthHook();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#193D6D]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <SystemNavbar />
      <main className="flex-1 w-full mx-auto pb-0 bg-[#F8FAFB]">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
};

const LanguageRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const savedLang = localStorage.getItem('provatio_language') || 'pt';
    const path = window.location.pathname;
    const pathParts = path.split('/');
    if (['pt', 'en', 'es'].includes(pathParts[1])) return;
    navigate(`/${savedLang}${path}${window.location.search}`, { replace: true });
  }, [navigate]);
  return null;
};

const ValidateLangRoute = () => {
  const { lang } = useParams();
  if (!['pt', 'en', 'es'].includes(lang)) {
    return <Navigate to="/pt" replace />;
  }
  return <Outlet />;
};

function App() {


  return (
    <ErrorBoundary>
      <Router>
        <LanguageProvider>
          <Helmet><title>{APP_CONFIG.APP_FULL_NAME}</title></Helmet>
          <AuthProvider>
            <ScrollToTop />
            <Routes>
              {/* Public API / Health Check Routes */}
              <Route path="/system-health-check" element={<SystemHealthCheck />} />
              {/* Main Redirect Route */}
              <Route path="/" element={<LanguageRedirect />} />

              {/* Application Routes enclosed in language validation */}
              <Route path="/:lang" element={<ValidateLangRoute />}>
                <Route index element={<HomePage />} />

                <Route path="system/login" element={<LoginPage />} />
                <Route path="login" element={<Navigate to="system/login" replace />} />

                <Route path="academia" element={<AcademyPage />} />
                <Route path="certificacao" element={<CertificacaoPage />} />
                <Route path="laboratorio" element={<LabsPage />} />
                <Route path="pesquisa" element={<ResearchPage />} />
                <Route path="assistente-tecnico" element={<AssistenteTecnicoPage />} />
                <Route path="compliance" element={<ComplianceDashboard />} />
                <Route path="inteligencia-forense" element={<IntelligenceDashboard />} />

                <Route path="admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

                <Route path="sistema" element={<AppLayout />}>
                  <Route index element={<SystemDashboard />} />

                  {/* Cadastros Routes */}
                  <Route path="cadastros" element={<ProtectedRoute><CadastrosSystemPage /></ProtectedRoute>} />
                  <Route path="cadastros/pessoas" element={<ProtectedRoute><PessoasPage /></ProtectedRoute>} />
                  <Route path="cadastros/tipo-pessoa" element={<ProtectedRoute><TipoPessoaPage /></ProtectedRoute>} />
                  <Route path="cadastros/palavra-chave" element={<ProtectedRoute><PalavraChavePage /></ProtectedRoute>} />
                  <Route path="cadastros/comarca-vara" element={<ProtectedRoute><ComarcaVaraPage /></ProtectedRoute>} />
                  <Route path="cadastros/situacao-processo" element={<ProtectedRoute><SituacaoProcessoPage /></ProtectedRoute>} />
                  <Route path="cadastros/natureza-processo" element={<ProtectedRoute><NaturezaProcessoPage /></ProtectedRoute>} />
                  <Route path="cadastros/acao-objeto" element={<ProtectedRoute><AcaoObjetoPage /></ProtectedRoute>} />
                  <Route path="cadastros/status-impugnacao" element={<ProtectedRoute><StatusImpugnacaoPage /></ProtectedRoute>} />

                  <Route path="processos" element={<ProtectedRoute><ProcessosSystemPage /></ProtectedRoute>} />
                  <Route path="tarefas" element={<ProtectedRoute><TarefasSystemPage /></ProtectedRoute>} />
                  <Route path="documentos" element={<ProtectedRoute><DocumentosSystemPage /></ProtectedRoute>} />
                  <Route path="agenda" element={<ProtectedRoute><AgendaSystemPage /></ProtectedRoute>} />
                  <Route path="financeiro" element={<ProtectedRoute><FinanceiroSystemPage /></ProtectedRoute>} />
                  <Route path="relatorios" element={<ProtectedRoute><RelatoriosSystemPage /></ProtectedRoute>} />
                  <Route path="historico" element={<ProtectedRoute><HistoricoSystemPage /></ProtectedRoute>} />
                  <Route path="auditoria" element={<ProtectedRoute><AuditoriaSystemPage /></ProtectedRoute>} />
                  <Route path="multiespecialistas" element={<MultiespecialistasPage />} />
                  <Route path="ajuda" element={<AjudaPage />} />
                </Route>

                <Route path="system" element={<Navigate to="../sistema" replace />} />

                {/* Catch-all language specific 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Global Catch-all 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster />
          </AuthProvider>
        </LanguageProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;